/**
 * Authentication Settings Page
 * Feature: System Administration Interface (001-admin-settings)
 */
import React, { useState, useEffect } from 'react';
import { Shield, Save, AlertCircle, CheckCircle, Users } from 'lucide-react';
import { getAuthPolicy, updateAuthPolicy, getNonCompliantUsers } from '../../api/auth-policy';
import type { AuthPolicy, AuthPolicyUpdate, NonCompliantUser } from '../../types/auth-policy';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Input from '../../components/Input';

const AuthenticationPage: React.FC = () => {
  const [policy, setPolicy] = useState<AuthPolicy | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [nonCompliantUsers, setNonCompliantUsers] = useState<NonCompliantUser[]>([]);
  const [showNonCompliant, setShowNonCompliant] = useState<boolean>(false);

  // Form state
  const [minPasswordLength, setMinPasswordLength] = useState<number>(12);
  const [requireUppercase, setRequireUppercase] = useState<boolean>(true);
  const [requireLowercase, setRequireLowercase] = useState<boolean>(true);
  const [requireDigit, setRequireDigit] = useState<boolean>(true);
  const [requireSpecialChar, setRequireSpecialChar] = useState<boolean>(true);
  const [passwordExpiryDays, setPasswordExpiryDays] = useState<number | null>(null);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number>(480);
  const [require2FA, setRequire2FA] = useState<boolean>(false);

  useEffect(() => {
    loadPolicy();
  }, []);

  const loadPolicy = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getAuthPolicy();
      setPolicy(data);

      // Populate form
      setMinPasswordLength(data.min_password_length);
      setRequireUppercase(data.require_uppercase);
      setRequireLowercase(data.require_lowercase);
      setRequireDigit(data.require_digit);
      setRequireSpecialChar(data.require_special_char);
      setPasswordExpiryDays(data.password_expiry_days ?? null);
      setSessionTimeoutMinutes(data.session_timeout_minutes);
      setRequire2FA(data.require_2fa);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load authentication policy';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadNonCompliantUsers = async () => {
    try {
      const users = await getNonCompliantUsers();
      setNonCompliantUsers(users);
      setShowNonCompliant(true);
    } catch (err) {
      console.error('Failed to load non-compliant users:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updates: AuthPolicyUpdate = {
        min_password_length: minPasswordLength,
        require_uppercase: requireUppercase,
        require_lowercase: requireLowercase,
        require_digit: requireDigit,
        require_special_char: requireSpecialChar,
        password_expiry_days: passwordExpiryDays,
        session_timeout_minutes: sessionTimeoutMinutes,
        require_2fa: require2FA
      };

      const updated = await updateAuthPolicy(updates);
      setPolicy(updated);
      setSuccessMessage('Authentication policy updated successfully');

      // Load non-compliant users after policy change
      await loadNonCompliantUsers();

      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update authentication policy';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (policy) {
      setMinPasswordLength(policy.min_password_length);
      setRequireUppercase(policy.require_uppercase);
      setRequireLowercase(policy.require_lowercase);
      setRequireDigit(policy.require_digit);
      setRequireSpecialChar(policy.require_special_char);
      setPasswordExpiryDays(policy.password_expiry_days ?? null);
      setSessionTimeoutMinutes(policy.session_timeout_minutes);
      setRequire2FA(policy.require_2fa);
    }
  };

  if (loading) {
    return (
      <div className="loading-container min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (error && !policy) {
    return (
      <div className="p-6">
        <div className="bg-error/10 dark:bg-error/20 border border-error/30 rounded-lg p-4">
          <p className="text-error">{error}</p>
          <Button variant="outline" onClick={loadPolicy} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          Authentication Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Configure password policies, session management, and two-factor authentication
        </p>
      </div>

      {successMessage && (
        <div className="mb-6 bg-success/10 dark:bg-success/20 border border-success/30 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
          <p className="text-success">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-error/10 dark:bg-error/20 border border-error/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
          <p className="text-error">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Password Policy */}
        <Card variant="elevated">
          <Card.Header title="Password Policy" />
          <Card.Body>
            <div className="space-y-4">
              <div>
                <Input
                  id="min-length"
                  type="number"
                  label="Minimum Password Length"
                  min={6}
                  max={128}
                  value={minPasswordLength}
                  onChange={(e) => setMinPasswordLength(parseInt(e.target.value, 10))}
                  helperText="Minimum: 6 characters, Maximum: 128 characters"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={requireUppercase}
                    onChange={(e) => setRequireUppercase(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Require at least one uppercase letter (A-Z)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={requireLowercase}
                    onChange={(e) => setRequireLowercase(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Require at least one lowercase letter (a-z)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={requireDigit}
                    onChange={(e) => setRequireDigit(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Require at least one digit (0-9)
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={requireSpecialChar}
                    onChange={(e) => setRequireSpecialChar(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Require at least one special character (!@#$%^&*...)
                  </span>
                </label>
              </div>

              <div>
                <Input
                  id="password-expiry"
                  type="number"
                  label="Password Expiry (Optional)"
                  min={30}
                  max={365}
                  value={passwordExpiryDays || ''}
                  onChange={(e) => setPasswordExpiryDays(e.target.value ? parseInt(e.target.value, 10) : null)}
                  placeholder="Leave blank for no expiry"
                  helperText="Force users to change passwords after this many days (30-365 days, or leave blank)"
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Session Management */}
        <Card variant="elevated">
          <Card.Header title="Session Management" />
          <Card.Body>
            <div>
              <Input
                id="session-timeout"
                type="number"
                label="Session Timeout (minutes)"
                min={5}
                max={43200}
                value={sessionTimeoutMinutes}
                onChange={(e) => setSessionTimeoutMinutes(parseInt(e.target.value, 10))}
                helperText="Automatically log out users after this period of inactivity (5-43200 minutes / 5min-30 days)"
              />
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                Common values: 480 (8 hours), 1440 (24 hours), 10080 (7 days)
              </p>
            </div>
          </Card.Body>
        </Card>

        {/* Two-Factor Authentication */}
        <Card variant="elevated">
          <Card.Header title="Two-Factor Authentication (2FA)" />
          <Card.Body>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={require2FA}
                onChange={(e) => setRequire2FA(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Require two-factor authentication for all users
              </span>
            </label>
            <div className="mt-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
              <p className="text-sm text-primary-800 dark:text-primary-300">
                <strong>Note:</strong> 2FA implementation is planned for a future release. 
                This setting prepares the database for when the feature becomes available.
              </p>
            </div>
          </Card.Body>
        </Card>

        {/* Non-Compliant Users */}
        {showNonCompliant && nonCompliantUsers.length > 0 && (
          <Card variant="elevated">
            <Card.Header 
              title={`Non-Compliant Users (${nonCompliantUsers.length})`}
              subtitle="These users need to update their passwords"
            />
            <Card.Body>
              <div className="bg-warning/10 dark:bg-warning/20 border border-warning/30 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-warning mb-1">
                      Password Policy Updated
                    </p>
                    <p className="text-sm text-warning">
                      The following users will be required to update their passwords on next login to comply with the new policy.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {nonCompliantUsers.map((user) => (
                  <div
                    key={user.user_id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{user.full_name || user.username}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                    <span className="text-xs text-warning bg-warning/10 dark:bg-warning/20 px-2 py-1 rounded">
                      Compliance Required
                    </span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            Reset
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving} isLoading={saving} leftIcon={Save}>
            {saving ? 'Saving...' : 'Save Policy'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationPage;
